import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Youtube, ExternalLink, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { getBackendUrl } from '../utils/getBackendUrl';

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

export const DailyVideo = () => {
  const [videoData, setVideoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchVideo();
  }, []);

  const fetchVideo = async () => {
    try {
      // Add cache-busting timestamp
      const response = await axios.get(`${API}/video-of-the-day?_t=${Date.now()}`);
      setVideoData(response.data);
    } catch (error) {
      console.error('Failed to fetch daily video:', error);
      setError('Unable to load video');
    } finally {
      setLoading(false);
    }
  };

  const getTodaysDate = () => {
    const today = new Date();
    return today.toLocaleDateString('en-US', { timeZone: 'America/Toronto', weekday: 'long', 
      month: 'long', 
      day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-lg shadow-md p-6 border border-red-100">
        <div className="flex items-center justify-center gap-3">
          <Loader2 className="w-5 h-5 animate-spin text-red-600" />
          <span className="text-sm text-red-600">Loading today's video...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return null;
  }

  if (!videoData) {
    return null;
  }

  return (
    <div className="bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 rounded-lg shadow-md p-6 border border-red-200">
      <div className="flex items-center gap-3 mb-4">
        <div className="bg-gradient-to-br from-red-500 to-orange-500 p-3 rounded-xl">
          <Youtube className="w-6 h-6 text-white" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-gray-900">Construction Video of the Day</h3>
          <p className="text-sm text-red-600">{getTodaysDate()}</p>
        </div>
      </div>
      
      <div className="bg-white/80 backdrop-blur-sm rounded-lg overflow-hidden border border-red-100">
        {/* Video Title */}
        <div className="p-4 pb-2">
          <h4 className="font-bold text-gray-900 text-base mb-1">
            🎬 {videoData.topic}
          </h4>
          <p className="text-gray-600 text-xs mb-2">
            {videoData.description}
          </p>
        </div>

        {/* Video Display - Embed if manual, preview card if AI */}
        {videoData.is_manual && videoData.embed_url ? (
          // Manual video set by admin - show embedded iframe
          <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
            <iframe
              className="absolute top-0 left-0 w-full h-full"
              src={videoData.embed_url}
              title={videoData.title}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        ) : (
          // AI-generated video - show clickable preview card
          <div 
            className="relative w-full bg-gradient-to-br from-red-900/90 to-orange-800/90 flex items-center justify-center cursor-pointer hover:from-red-800 hover:to-orange-700 transition-all group"
            style={{ paddingBottom: '56.25%' }}
            onClick={() => {
              const url = videoData.watch_url || `https://www.youtube.com/results?search_query=${encodeURIComponent(videoData.topic)}+construction`;
              window.open(url, '_blank');
            }}
          >
            <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center">
              <div className="bg-white/20 backdrop-blur-sm rounded-full p-6 mb-4 group-hover:scale-110 transition-transform">
                <Youtube className="w-16 h-16 text-white" />
              </div>
              <p className="text-white font-bold text-xl mb-2 px-4">
                {videoData.topic}
              </p>
              <p className="text-white/90 text-sm mb-4 px-6">
                Click to watch on YouTube
              </p>
              <div className="flex items-center gap-2 text-xs text-white/80 bg-white/10 px-4 py-2 rounded-full">
                <ExternalLink className="w-3 h-3" />
                <span>Opens in new tab</span>
              </div>
            </div>
          </div>
        )}

        {/* Watch on YouTube Button */}
        <div className="p-3">
          <Button
            onClick={() => {
              const url = videoData.watch_url || videoData.search_url || `https://www.youtube.com/results?search_query=${encodeURIComponent(videoData.topic)}+construction`;
              window.open(url, '_blank');
            }}
            className="w-full bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white font-semibold"
            size="sm"
          >
            <Youtube className="w-4 h-4 mr-2" />
            Watch on YouTube
            <ExternalLink className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
      
      <div className="mt-4 text-center">
        <p className="text-xs text-gray-500">
          🤖 AI-Curated • Updated Daily • Learn Something New • v2.0
        </p>
      </div>
    </div>
  );
};
